import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd, lockScroll, unlockScroll } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #scrollState;
  #transition = 0;
  #closing = false;

  #lockPage() {
    if (this.#scrollState) return;
    const root = document.documentElement;
    const wrapper = this.closest('.page-wrapper');
    this.#scrollState = {
      x: window.scrollX, y: window.scrollY, wrapper,
      wrapperTop: wrapper?.scrollTop ?? 0,
      wrapperLeft: wrapper?.scrollLeft ?? 0,
      top: root.style.getPropertyValue('--gp-menu-scroll-top'),
    };
    root.style.setProperty('--gp-menu-scroll-top', `${-window.scrollY}px`);
    root.setAttribute('data-gp-menu-open', '');
    lockScroll(this);
  }

  #unlockPage() {
    const state = this.#scrollState;
    if (!state) return;
    this.#scrollState = undefined;
    const root = document.documentElement;
    root.removeAttribute('data-gp-menu-open');
    if (state.top) root.style.setProperty('--gp-menu-scroll-top', state.top);
    else root.style.removeProperty('--gp-menu-scroll-top');
    unlockScroll(this);
    state.wrapper?.scrollTo({left: state.wrapperLeft, top: state.wrapperTop, behavior: 'instant'});
    window.scrollTo({left: state.x, top: state.y, behavior: 'instant'});
  }

  #finishClose = (restoreFocus = false) => {
    ++this.#transition;
    this.#closing = false;
    reset(this.refs.details);
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
    removeTrapFocus();
    this.#unlockPage();
    if (restoreFocus) this.refs.details.querySelector('summary')?.focus({preventScroll: true});
  };

  #onNavigation = (event) => {
    if (this.isOpen && event.target instanceof Element && event.target.closest('a[href]')) {
      this.#finishClose();
    }
  };

  #onNativeToggle = () => {
    if (!this.isOpen && this.#scrollState) this.#finishClose();
    else if (this.isOpen && !this.#scrollState) this.open();
  };

  #onResize = () => {
    if (this.#scrollState && !this.refs.details.querySelector('summary')?.getClientRects().length) {
      this.#finishClose();
    }
  };

  #onPageHide = () => {
    if (this.#scrollState) this.#finishClose();
  };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onNavigation);
    this.refs.details.addEventListener('toggle', this.#onNativeToggle);
    window.addEventListener('resize', this.#onResize);
    window.addEventListener('pagehide', this.#onPageHide);
    this.#setupAnimatedElementListeners();
    if (this.isOpen) this.open();
  }

  disconnectedCallback() {
    if (this.#scrollState) this.#finishClose();
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onNavigation);
    this.refs.details.removeEventListener('toggle', this.#onNativeToggle);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('pagehide', this.#onPageHide);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen && !this.#closing ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    let transition;
    if (details === this.refs.details) {
      transition = ++this.#transition;
      this.#closing = false;
      this.#lockPage();
      details.open = true;
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (details === this.refs.details && transition !== this.#transition) return;
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      trapFocus(details);
      if (details === this.refs.details) {
        this.refs.menuDrawer.querySelector('button')?.focus({preventScroll: true});
      }
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    if (details === this.refs.details) {
      if (!this.isOpen || this.#closing) return;
      this.#closing = true;
      const transition = ++this.#transition;
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      details.classList.remove('menu-open');
      const complete = () => {
        if (transition === this.#transition) this.#finishClose(true);
      };
      requestAnimationFrame(() => onAnimationEnd(this.refs.menuDrawer, complete, {subtree: false}));
      setTimeout(complete, 450);
      return;
    }
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (!this.isOpen || !this.#scrollState) return;
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
